import React, { useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import StarIcon from '@mui/icons-material/Star';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import RateReviewIcon from '@mui/icons-material/RateReview';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const SectionContainer = styled(Box)({
  padding: '120px 60px',
  background: 'linear-gradient(180deg, #0f0f18 0%, #0a0a0f 100%)',
  position: 'relative',
  overflow: 'hidden',
  '@media (max-width: 1024px)': {
    padding: '100px 32px'
  },
  '@media (max-width: 768px)': {
    padding: '80px 20px'
  }
});

const BackgroundAccent = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '800px',
  height: '800px',
  borderRadius: '50%',
  background: 'radial-gradient(circle, rgba(251, 191, 36, 0.06) 0%, transparent 60%)',
  pointerEvents: 'none'
});

const ContentWrapper = styled(Box)({
  maxWidth: '1200px',
  margin: '0 auto',
  position: 'relative',
  zIndex: 10
});

const SectionHeader = styled(Box)({
  textAlign: 'center',
  marginBottom: '64px',
  animation: `${fadeInUp} 0.8s ease-out`
});

const SectionBadge = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  padding: '10px 24px',
  background: 'rgba(251, 191, 36, 0.1)',
  border: '1px solid rgba(251, 191, 36, 0.2)',
  borderRadius: '50px',
  marginBottom: '24px'
});

const BadgeText = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '12px',
  fontWeight: 600,
  color: '#fbbf24',
  textTransform: 'uppercase',
  letterSpacing: '2px'
});

const SectionTitle = styled(Typography)({
  fontFamily: "'Outfit', sans-serif",
  fontSize: 'clamp(36px, 5vw, 56px)',
  fontWeight: 700,
  color: '#ffffff',
  lineHeight: 1.2,
  letterSpacing: '-1px'
});

const GradientText = styled('span')({
  background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 50%, #fcd34d 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text'
});

const CarouselWrapper = styled(Box)({
  position: 'relative'
});

const CarouselTrack = styled(Box)({
  display: 'flex',
  gap: '24px',
  transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
});

const TestimonialCard = styled(Box)(({ active }) => ({
  flex: '0 0 100%',
  maxWidth: '800px',
  margin: '0 auto',
  padding: '48px',
  background: active
    ? 'linear-gradient(135deg, rgba(251, 191, 36, 0.08) 0%, rgba(245, 158, 11, 0.04) 100%)'
    : 'rgba(255, 255, 255, 0.03)',
  borderRadius: '28px',
  border: active
    ? '1px solid rgba(251, 191, 36, 0.2)'
    : '1px solid rgba(255, 255, 255, 0.06)',
  textAlign: 'center',
  transition: 'all 0.5s ease',
  animation: `${fadeInUp} 0.8s ease-out`,
  '@media (max-width: 768px)': {
    padding: '32px 24px'
  }
}));

const QuoteIcon = styled(Box)({
  width: '64px',
  height: '64px',
  borderRadius: '50%',
  background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.15) 0%, rgba(245, 158, 11, 0.1) 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto 32px',
  '& svg': {
    fontSize: '32px',
    color: '#fbbf24'
  }
});

const TestimonialText = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: 'clamp(18px, 2.5vw, 24px)',
  fontWeight: 400,
  color: 'rgba(255, 255, 255, 0.85)',
  lineHeight: 1.8,
  marginBottom: '32px',
  fontStyle: 'italic'
});

const RatingStars = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  gap: '6px',
  marginBottom: '24px'
});

const AuthorSection = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '8px'
});

const AuthorAvatar = styled(Box)({
  width: '56px',
  height: '56px',
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: "'Outfit', sans-serif",
  fontSize: '22px',
  fontWeight: 600,
  color: '#0a0a0f',
  marginBottom: '8px'
});

const AuthorName = styled(Typography)({
  fontFamily: "'Outfit', sans-serif",
  fontSize: '18px',
  fontWeight: 600,
  color: '#ffffff'
});

const AuthorTitle = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '14px',
  fontWeight: 400,
  color: 'rgba(255, 255, 255, 0.5)'
});

const NavigationButtons = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  gap: '16px',
  marginTop: '48px'
});

const NavButton = styled(IconButton)({
  width: '52px',
  height: '52px',
  borderRadius: '14px',
  background: 'rgba(255, 255, 255, 0.05)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  color: 'rgba(255, 255, 255, 0.7)',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
    borderColor: '#fbbf24',
    color: '#0a0a0f',
    transform: 'translateY(-3px)',
    boxShadow: '0 8px 25px rgba(251, 191, 36, 0.3)'
  }
});

const DotsContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  gap: '10px',
  marginTop: '32px'
});

const Dot = styled(Box)(({ active }) => ({
  width: active ? '32px' : '10px',
  height: '10px',
  borderRadius: '5px',
  background: active
    ? 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)'
    : 'rgba(255, 255, 255, 0.2)',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: active
      ? 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)'
      : 'rgba(255, 255, 255, 0.4)'
  }
}));

const TestimonialsSection = ({ testimonials }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Filter to only show 5-star reviews
  const fiveStarTestimonials = (testimonials || []).filter(t => {
    const rating = t.rating || 5;
    return rating === 5;
  });

  if (fiveStarTestimonials.length === 0) return null;

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? fiveStarTestimonials.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === fiveStarTestimonials.length - 1 ? 0 : prev + 1));
  };

  const currentTestimonial = fiveStarTestimonials[currentIndex];

  return (
    <SectionContainer id="reviews">
      <BackgroundAccent />
      <ContentWrapper>
        <SectionHeader>
          <SectionBadge>
            <RateReviewIcon sx={{ fontSize: 16, color: '#fbbf24' }} />
            <BadgeText>Testimonials</BadgeText>
          </SectionBadge>
          <SectionTitle>
            Client <GradientText>Reviews</GradientText>
          </SectionTitle>
        </SectionHeader>

        <CarouselWrapper>
          <TestimonialCard active>
            <QuoteIcon>
              <FormatQuoteIcon />
            </QuoteIcon>

            <RatingStars>
              {[1, 2, 3, 4, 5].map((star) => (
                <StarIcon
                  key={star}
                  sx={{
                    fontSize: 24,
                    color: star <= (currentTestimonial.rating || 5) ? '#fbbf24' : 'rgba(255, 255, 255, 0.2)'
                  }}
                />
              ))}
            </RatingStars>

            <TestimonialText>
              "{currentTestimonial.text || currentTestimonial.comment || currentTestimonial.review}"
            </TestimonialText>

            <AuthorSection>
              <AuthorAvatar>
                {(currentTestimonial.author || currentTestimonial.name || 'A').charAt(0).toUpperCase()}
              </AuthorAvatar>
              <AuthorName>
                {currentTestimonial.author || currentTestimonial.name || 'Anonymous'}
              </AuthorName>
              <AuthorTitle>
                {currentTestimonial.title || currentTestimonial.role || 'Verified Customer'}
              </AuthorTitle>
            </AuthorSection>
          </TestimonialCard>
        </CarouselWrapper>

        {fiveStarTestimonials.length > 1 && (
          <>
            <NavigationButtons>
              <NavButton onClick={handlePrev}>
                <ArrowBackIosNewIcon sx={{ fontSize: 18 }} />
              </NavButton>
              <NavButton onClick={handleNext}>
                <ArrowForwardIosIcon sx={{ fontSize: 18 }} />
              </NavButton>
            </NavigationButtons>

            <DotsContainer>
              {fiveStarTestimonials.map((_, index) => (
                <Dot
                  key={index}
                  active={index === currentIndex}
                  onClick={() => setCurrentIndex(index)}
                />
              ))}
            </DotsContainer>
          </>
        )}
      </ContentWrapper>
    </SectionContainer>
  );
};

export default TestimonialsSection;
